import { useState } from "react";
import { Outlet, NavLink } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import BarChartIcon from "@mui/icons-material/BarChart";
import GavelIcon from "@mui/icons-material/Gavel";
import FlagIcon from "@mui/icons-material/Flag";
import PeopleIcon from "@mui/icons-material/People";
import StoreIcon from "@mui/icons-material/Store";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CommentIcon from "@mui/icons-material/Comment";
import HomeIcon from "@mui/icons-material/Home";
import LogoutIcon from "@mui/icons-material/Logout";
import { frontendRoutes } from "../../utility/frontendRoutes";
import useAuthentication from "../../hooks/useAuthentication";
import NgwaahiaIcon from "../../common-components/NgwaahiaIcon";

const DRAWER_WIDTH = 220;

const navItems = [
  {
    label: "Dashboard",
    icon: <DashboardIcon />,
    to: frontendRoutes.ADMIN_ROOT,
  },
  {
    label: "Orders",
    icon: <ReceiptLongIcon />,
    to: frontendRoutes.ADMIN_ORDERS,
  },
  { label: "Disputes", icon: <GavelIcon />, to: frontendRoutes.ADMIN_DISPUTES },
  { label: "Reports", icon: <FlagIcon />, to: frontendRoutes.ADMIN_REPORTS },
  { label: "Users", icon: <PeopleIcon />, to: frontendRoutes.ADMIN_USERS },
  { label: "Shops", icon: <StoreIcon />, to: frontendRoutes.ADMIN_SHOPS },
  {
    label: "Products",
    icon: <Inventory2Icon />,
    to: frontendRoutes.ADMIN_PRODUCTS,
  },
  {
    label: "Verifications",
    icon: <VerifiedUserIcon />,
    to: frontendRoutes.ADMIN_VERIFICATIONS,
  },
  {
    label: "Feature Flags",
    icon: <ToggleOnIcon />,
    to: frontendRoutes.ADMIN_FEATURE_FLAGS,
  },
  {
    label: "Analytics",
    icon: <BarChartIcon />,
    to: frontendRoutes.ADMIN_ANALYTICS,
  },
  {
    label: "Feedback",
    icon: <CommentIcon />,
    to: frontendRoutes.ADMIN_FEEDBACK,
  },
];

const NavItems = ({ onClick, onLogout, onUser }) => (
  <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
    <List sx={{ flex: 1 }}>
      {navItems.map(({ label, icon, to }) => (
        <ListItemButton
          key={label}
          component={NavLink}
          to={to}
          end={to === frontendRoutes.ADMIN_ROOT}
          onClick={onClick}
          sx={{
            "&.active": {
              bgcolor: "primary.light",
              color: "primary.contrastText",
            },
            "&.active .MuiListItemIcon-root": { color: "primary.contrastText" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>{icon}</ListItemIcon>
          <ListItemText primary={label} />
        </ListItemButton>
      ))}
    </List>
    <Divider />
    {onUser && (
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          Signed in as
        </Typography>
        <Typography variant="body2" fontWeight={600} noWrap>
          {onUser}
        </Typography>
      </Box>
    )}
    <Divider />
    <List>
      <ListItemButton
        component={NavLink}
        to={frontendRoutes.HOME}
        onClick={onClick}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <HomeIcon />
        </ListItemIcon>
        <ListItemText primary="Go to App" />
      </ListItemButton>
      <ListItemButton
        onClick={() => {
          onClick?.();
          onLogout();
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <LogoutIcon />
        </ListItemIcon>
        <ListItemText primary="Log out" />
      </ListItemButton>
    </List>
  </Box>
);

const AdminLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { handleLogout, currentUser } = useAuthentication();

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Toolbar sx={{ gap: 1 }}>
        <NgwaahiaIcon size={28} color="#046287" />
        <Typography variant="subtitle1" fontWeight={700} color="primary">
          Admin Portal
        </Typography>
      </Toolbar>
      <Divider />
      <NavItems
        onClick={() => isMobile && setMobileOpen(false)}
        onLogout={handleLogout}
        onUser={currentUser?.firstName}
      />
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {isMobile ? (
        <>
          <AppBar position="fixed" color="default" elevation={1}>
            <Toolbar>
              <IconButton
                edge="start"
                onClick={() => setMobileOpen(true)}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
              <NgwaahiaIcon
                size={24}
                color="#046287"
                style={{ marginRight: 6 }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  lineHeight={1.2}
                >
                  Admin Portal
                </Typography>
                {currentUser?.firstName && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    lineHeight={1}
                  >
                    {currentUser.firstName}
                  </Typography>
                )}
              </Box>
              <IconButton
                component={NavLink}
                to={frontendRoutes.HOME}
                title="Go to App"
              >
                <HomeIcon />
              </IconButton>
              <IconButton onClick={handleLogout} title="Log out" edge="end">
                <LogoutIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ "& .MuiDrawer-paper": { width: DRAWER_WIDTH } }}
          >
            {drawerContent}
          </Drawer>
          <Box component="main" sx={{ flex: 1, p: 2, mt: 8 }}>
            <Outlet />
          </Box>
        </>
      ) : (
        <>
          <Drawer
            variant="permanent"
            sx={{
              width: DRAWER_WIDTH,
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                width: DRAWER_WIDTH,
                boxSizing: "border-box",
              },
            }}
          >
            {drawerContent}
          </Drawer>
          <Box
            component="main"
            sx={{ flex: 1, p: 3, bgcolor: "grey.50", minHeight: "100vh" }}
          >
            <Outlet />
          </Box>
        </>
      )}
    </Box>
  );
};

export default AdminLayout;
